<script>
import Layout from '@layouts/main.vue'
import {
  documentMethods,
  documentComputed,
  authComputed,
  fileComputed,
  fileMethods,
} from '@state/helpers'
import debounce from 'lodash/debounce'

export default {
  page: {
    title: 'Dokumentai',
  },

  components: { Layout },

  data() {
    return {
      keyword: '',
      page: 1,
      perPage: 25,
      dialogs: {
        files: {
          visible: false,
        },
      },
    }
  },

  computed: {
    ...authComputed,

    ...documentComputed,

    ...fileComputed,
  },

  created() {
    this.fetchData()
  },

  methods: {
    ...documentMethods,

    ...fileMethods,

    fetchData() {
      const { keyword, page, perPage } = this
      this.fetchAllDocuments({ keyword, page, perPage })
    },

    onChangeSearchKeyword: debounce(function () {
      this.fetchData()
    }, 500),

    changePage(page) {
      this.page = page
      this.fetchData()
    },

    onCloseFilesDialog() {
      this.dialogs.files.visible = false
    },

    onClickFiles(documentId) {
      this.fetchAllFiles(documentId).then(
        () => (this.dialogs.files.visible = true),
      )
    },
  },
}
</script>

<template>
  <Layout>
    <base-page-title>
      <template v-slot:title>
        <h1 class="text-2xl font-bold"> Dokumentai </h1>
        <div
          v-if="documentsPagination"
          class="text-sm text-gray-600 font-medium -mt-2"
        >
          {{ documentsPagination.totalItems | countDisplay }}
        </div>
      </template>
      <template v-slot:actions>
        <div class="mb-4">
          <base-input
            v-model="keyword"
            type="text"
            placeholder="Paieškos raktažodis..."
            class="w-full input rounded"
            autofocus
            @change="onChangeSearchKeyword"
          />
        </div>
        <div>
          <base-btn
            v-if="currentUser.role !== 'volunteer'"
            class="lg:ml-4 py-2 px-4"
            @click="$router.push({ name: 'document-create' })"
          >
            <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />
            Sukurti dokumentą
          </base-btn>
        </div>
      </template>
    </base-page-title>

    <div class="-mx-4 lg:-mx-8">
      <div v-if="documents && documents.length" class="overflow-x-auto">
        <table
          v-if="currentUser.role !== 'volunteer'"
          class="table-auto w-full table-fixed"
        >
          <thead>
            <tr>
              <th class="text-left">Pavadinimas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="document in documents" :key="document.id" class="border">
              <td class="px-4 py-2">
                <div class="flex flex-col sm:flex-row">
                  <div class="flex-grow pr-2 min-w-0">
                    <router-link
                      :to="{
                        name: 'document-edit',
                        params: { id: document.id },
                      }"
                      class="mb-2 text-wrap"
                    >
                      {{ document.title }}
                    </router-link>
                    <div
                      v-if="document.description"
                      class="text-sm opacity-75 mb-2 whitespace-normal"
                    >
                      {{ document.description }}
                    </div>
                  </div>
                  <div class="-mr-4 flex-shrink-0">
                    <base-btn
                      class="mr-2 px-2 py-1"
                      type="secondary"
                      @click="
                        $router.push({
                          name: 'document-preview',
                          params: { id: document.id },
                        })
                      "
                    >
                      Failai
                    </base-btn>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table v-else class="table-auto w-full table-fixed">
          <thead>
            <tr>
              <th class="text-left">Pavadinimas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="document in documents" :key="document.id" class="border">
              <td class="px-4 py-2">
                <div class="flex flex-row sm:flex-row">
                  <div class="flex-grow pr-2 min-w-0">
                    <div class="mb-2">
                      {{ document.title }}
                    </div>
                    <div
                      v-if="document.description"
                      class="text-sm opacity-75 mb-2 whitespace-normal"
                    >
                      {{ document.description }}
                    </div>
                  </div>
                  <div class="-mr-4">
                    <base-btn
                      v-if="document.can_edit"
                      class="mr-2 px-2 py-1"
                      type="secondary"
                      @click="
                        $router.push({
                          name: 'document-preview',
                          params: { id: document.id },
                        })
                      "
                    >
                      Failai
                    </base-btn>
                    <base-btn
                      v-else
                      class="px-2 py-1"
                      type="secondary"
                      @click="onClickFiles(document.id)"
                    >
                      Failai
                    </base-btn>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <base-pagination :pagination="documentsPagination" @change="changePage" />

    <base-dialog
      v-if="dialogs.files.visible"
      title="Failai"
      :overlay-closes="true"
      @close="onCloseFilesDialog"
    >
      <div class="-mx-8">
        <table class="table-auto w-full">
          <thead>
            <tr>
              <th>Pavadinimas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="file in files" :key="file.id">
              <td class="py-2">
                <div class="flex flex-col">
                  <div class="flex-grow">
                    <base-upload-link
                      v-if="file.file_path"
                      :filename="file.file_path"
                    >
                      {{ file.file_name }}
                    </base-upload-link>
                    <a v-else :href="file.url" target="_blank">
                      {{ file.url }}
                    </a>
                  </div>
                </div>
              </td>
            </tr>
            <tr v-if="!files.length">
              <td class="text-sm"> Nėra jokių įkeltų failų. </td>
            </tr>
          </tbody>
        </table>
      </div>
    </base-dialog>
  </Layout>
</template>
